import { mobileAccess } from "#skyward_global";
import { ButtonDefinition, ButtonIcon, ButtonText, ModalDialog, showDialog } from "#skyward_modalLayer";
import { registerLoadDOMCallback } from "#skyward_window";
import { setValue as setControlValue } from "./skyward.ui.controls";

const maximumDate = "Maximum Date";
const minimumDate = "Minimum Date";
const currentDate = "Current Date";
const fromNowDirection = "From Now";
const agoDirection = "Ago";
const standardYears = 250;
const selectRelativeDate = "Select Relative Date";

const dateParts =
{
    Year: "Year",
    Day: "Day",
};

const classNames =
{
    datePicker: "datepicker",
    datePickerWrapper: "datepicker-wrapper",
    hidden: "hidden",
    launchRelativeDatePicker: "launchRelativeDatePicker",
    launchRelativeDatePickerUnbound: "launchRelativeDatePickerUnbound",
    masked: "masked",
    predefinedDateSelector: "predefinedDateSelector",
    readonly: "readonly",
    relativeDate: "relativeDate",
    relativeDateUnit: "relativeDateUnit",
    relativeDateDatePart: "relativeDateDatePart",
    relativeDateDirection: "relativeDateDirection",
    relativeDateDisplayUnbound: "relativeDateDisplayUnbound"
};

export class RelativeDate
{
    readonly Unit: number;
    readonly DatePart: string;
    readonly Direction: string;

    constructor(unit: number, datePart: string, direction: string)
    {
        this.Unit = unit;
        this.DatePart = datePart;
        this.Direction = direction;
        Object.freeze(this);
    }

    toString(): string
    {
        if (isNaN(this.Unit))
        {
            return "";
        }

        if (this.Unit === 0)
        {
            return currentDate;
        }

        if (this.Unit === standardYears && this.DatePart === dateParts.Year)
        {
            if (this.Direction === fromNowDirection)
            {
                return maximumDate;
            }

            return minimumDate;
        }

        const datePartDisplay = this.formatDatePartForDisplay();

        return `${this.Unit} ${datePartDisplay} ${this.Direction}`;
    }

    private formatDatePartForDisplay(): string
    {
        let datePartDisplay = this.DatePart;
        //formatting for how DateParts enum is setup
        if (datePartDisplay === "WeekDay")
        {
            datePartDisplay = "Weekday"
        }
        return (this.Unit === 1) ? datePartDisplay : `${datePartDisplay}s`
    }

    static parse(valueObj: any): RelativeDate | null
    {
        if (valueObj instanceof RelativeDate)
        {
            return valueObj;
        }
        if (valueObj === null || valueObj === undefined)
        {
            return null;
        }
        const value = valueObj.toString();

        if (value === maximumDate)
        {
            return new RelativeDate(standardYears, dateParts.Year, fromNowDirection);
        }
        else if (value === minimumDate)
        {
            return new RelativeDate(standardYears, dateParts.Year, agoDirection);
        }
        else if (value === currentDate)
        {
            return new RelativeDate(0, dateParts.Day, agoDirection);
        }

        const relativeDatePattern = /^(\d+) (Weekday|Day|Week|Month|Year)s? (Ago|From Now)$/i;
        const parts = relativeDatePattern.exec(value);
        if (parts === null)
        {
            return null;
        }
        return new RelativeDate(parseInt(parts[1]), parts[2], parts[3]);
    }
}

export function setValue(datePickerID: string, value: any): void
{
    const date = RelativeDate.parse(value);
    $("#" + datePickerID).val("");

    if (date === null)
    {
        showDatePicker(datePickerID);
        return;
    }

    showRelativeDateDisplay(datePickerID, date.toString());
    updateLaunchButtonData(datePickerID, date);
}

export function parseRelativeDateString(value: any): RelativeDate | null
{
    return RelativeDate.parse(value);
}

export function showRelativeDateDisplay(datePickerID: string, value: string): void
{
    getRelativeDateDisplay(datePickerID).text(value).removeClass(classNames.hidden);

    const datepicker = $("#" + datePickerID);

    datepicker.addClass(classNames.relativeDate);
    datepicker.data("relativeDate", value);
    datepicker.addClass(classNames.hidden);
    datepicker.trigger("dirty");
    datepicker.attr("data-relative-date", value);
    $("#" + datePickerID + "-errorLabel").addClass(classNames.hidden).empty();
}

export function showDatePicker(datePickerID: string): JQuery<HTMLElement>
{
    const relativeDateDisplay = getRelativeDateDisplay(datePickerID);

    if (relativeDateDisplay.length === 0)
    {
        return $();
    }

    const datepicker = $("#" + datePickerID);
    relativeDateDisplay.addClass(classNames.hidden);
    datepicker.removeClass(classNames.relativeDate);
    datepicker.removeClass(classNames.hidden);
    datepicker.data("relativeDate", null);
    $("#" + datePickerID + "-errorLabel").removeClass(classNames.hidden);
    return datepicker;
}

export function getRelativeDateDisplay(datePickerID: string): JQuery<HTMLElement>
{
    return $("#" + datePickerID + "-relativeDateDisplay");
}

function getRelativeDateBuilder(datePickerID: string): JQuery<HTMLElement>
{
    return $("#" + datePickerID + "-relativeDateBuilder");
}

function getRelativeDateLaunchButton(datePickerID: string): JQuery<HTMLElement>
{
    return $("#" + datePickerID + "-relativeDateBuilderLaunchButton");
}

function initRelativeDatePicker(selector: string): void
{
    $(`${selector} .${classNames.launchRelativeDatePickerUnbound}`).removeClass(classNames.launchRelativeDatePickerUnbound)
        .on("click", launchRelativeDatePicker)
        .on("keydown", launchRelativeDatePickerOnEnter);

    $(`${selector} .${classNames.relativeDateDisplayUnbound}`).removeClass(classNames.relativeDateDisplayUnbound).each(function ()
    {
        const display = $(this);
        display.on("click", launchRelativeDatePicker);
        display.on("keydown", launchRelativeDatePickerOnEnter);

        const datepicker = display.siblings(`.${classNames.datePickerWrapper}`).children(`.${classNames.datePicker}`);
        const label = datepicker.findLabel();
        if (label.length > 0)
        {
            display.attr("aria-labelledby", label.addIds().attr("id")!);
        }
        if (datepicker.is("[readonly]"))
        {
            display.siblings(`.${classNames.launchRelativeDatePicker}`).hide();
        }
    });
}

function launchRelativeDatePickerOnEnter(this: HTMLElement, event: JQuery.KeyDownEvent): void
{
    const key = skyward.keys.getKeyNamesFromEvent(event);
    if (key.ENTER || key.SPACE)
    {
        launchRelativeDatePicker.call(this);
    }
}

function launchRelativeDatePicker(this: HTMLElement): void
{
    const button = $(this);
    if (button.hasClass(classNames.readonly))
    {
        return;
    }
    const datePickerID: string = button.data("relatedDatePicker");
    const builder = getRelativeDateBuilder(datePickerID);

    const buttonData = button.data();

    const unitBox = builder.find(`.${classNames.relativeDateUnit}`);
    const value = (buttonData.unit !== null && buttonData.unit !== undefined && !isNaN(buttonData.unit)) ? buttonData.unit : "";
    setControlValue(unitBox, value, true);
    unitBox.removeClass(classNames.masked);

    builder.find(`.${classNames.relativeDateDatePart} > [selected], .${classNames.relativeDateDirection} > [selected]`).removeAttr("selected"); //reset all selected values
    builder.find(`.${classNames.relativeDateDatePart} > [value='${(buttonData.datePart || dateParts.Day)}']`).attr("selected", "selected");
    builder.find(`.${classNames.relativeDateDirection} > [value='${(buttonData.direction || agoDirection)}']`).attr("selected", "selected");

    const dialogOptions =
    {
        closeOnClick: true,
        closeOnEscape: true,
        closeOnGlassClick: true,
        anchorTo: mobileAccess ? null : button
    };

    const buttons: ButtonDefinition[] =
    [
        {
            text: ButtonText.Save,
            click: onSave,
            icon: ButtonIcon.Save
        },
        {
            text: ButtonText.Cancel,
            click: close,
            icon: ButtonIcon.Cancel
        }
    ];

    const modal = showDialog(selectRelativeDate, builder.children().clone(), buttons, dialogOptions);
    modal.content.data("relatedDatePicker", datePickerID);

    modal.content.find(`.${classNames.relativeDateUnit}`).trigger("focus");

    modal.content.find(`.${classNames.predefinedDateSelector}`).on("click", function ()
    {
        selectPredefinedDate(modal, $(this).text().trim());
    });
}

function selectPredefinedDate(modal: ModalDialog, value: string): void
{
    const datePickerID: string = modal.content.data("relatedDatePicker");

    let date: RelativeDate;
    if (value === maximumDate)
    {
        date = new RelativeDate(250, dateParts.Year, fromNowDirection);
    }
    else if (value === minimumDate)
    {
        date = new RelativeDate(250, dateParts.Year, agoDirection);
    }
    else
    {
        date = new RelativeDate(0, dateParts.Day, agoDirection);
    }

    showRelativeDateDisplay(datePickerID, date.toString());
    updateLaunchButtonData(datePickerID, date);
    modal.close();
}

function updateLaunchButtonData(datePickerID: string, value: RelativeDate): void
{
    const buttonData = getRelativeDateLaunchButton(datePickerID).data();
    buttonData.unit = value.Unit;
    buttonData.datePart = value.DatePart;
    buttonData.direction = value.Direction;

    const displayData = getRelativeDateDisplay(datePickerID).data();
    displayData.unit = value.Unit;
    displayData.datePart = value.DatePart;
    displayData.direction = value.Direction;
}

function onSave(_event: JQuery.TriggeredEvent, modal: ModalDialog): void
{
    const datePickerID: string = modal.content.data("relatedDatePicker");
    const unitValue = modal.content.find(`.${classNames.relativeDateUnit}`).val() as string;
    const unit = parseInt(unitValue);
    const datePart: string = modal.content.find(`.${classNames.relativeDateDatePart}`).val() as string;
    const direction: string = modal.content.find(`.${classNames.relativeDateDirection}`).val() as string;

    const result = new RelativeDate(unit, datePart, direction);
    $("#" + datePickerID).val("");
    showRelativeDateDisplay(datePickerID, result.toString());
    updateLaunchButtonData(datePickerID, result);

    modal.close();
}

function close(_event: JQuery.TriggeredEvent, modal: ModalDialog): void
{
    modal.close();
}

const skyward = window.skyward;
skyward.relativedatepicker = { setValue, parseRelativeDateString, showRelativeDateDisplay, showDatePicker, getRelativeDateDisplay };

registerLoadDOMCallback(initRelativeDatePicker);